import moment from "moment";

import { getSkipReason } from "../helpers";

const formatDay = (value?: string | null) =>
  value ? moment(value).format("MMM D, YYYY") : "not set";

export const formatScheduleValue = (
  field: string,
  value: string | number | null,
) => {
  if (field.endsWith("_offset")) {
    return typeof value === "number"
      ? `${value} ${value === 1 ? "day" : "days"} after employee start date`
      : "not set";
  }
  return formatDay(typeof value === "string" ? value : null);
};

export const formatReminderSchedule = (
  scheduledDate?: string | null,
  daysAfterStartDate?: number,
) => {
  if (scheduledDate) {
    return `due ${formatDay(scheduledDate)}`;
  }
  if (typeof daysAfterStartDate === "number") {
    return `${daysAfterStartDate} ${
      daysAfterStartDate === 1 ? "day" : "days"
    } after kickoff`;
  }
  return "";
};

/** A reason is a notification skip reason only when the event was skipped. */
export const getActivitySkipReason = (
  status: string | null | undefined,
  payload: unknown,
) => (status === "skipped" ? getSkipReason(payload) : null);

/**
 * "138 sent, 3 failed, 4 skipped", leaving out the zeroes. A batch with nothing to
 * count, like several people calibrated in one click, says how many people it touched.
 */
export const getBatchDetail = (batch: {
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  rowCount: number;
}) =>
  [
    batch.sentCount > 0 ? `${batch.sentCount} sent` : null,
    batch.failedCount > 0 ? `${batch.failedCount} failed` : null,
    batch.skippedCount > 0 ? `${batch.skippedCount} skipped` : null,
  ]
    .filter(Boolean)
    .join(", ") || `${batch.rowCount} people`;

type QuestionChangePayload = {
  title?: string;
  scope?: string;
  question_ids?: number[];
  changes?: {
    field: string;
    from: string | number | null;
    to: string | number | null;
  }[];
};

const quoted = (value: string | number | null) => `"${value ?? ""}"`;

/**
 * The details of a change to a published cycle's questions: what changed, the old and the
 * new title, and where it applies. For example `title changed from "Comunication" to
 * "Communication" · everywhere`. A description can be long, so only the fact of its change
 * is shown; the old and new text stay in the payload.
 */
export const getQuestionChangeDetails = (
  verb: string,
  payload: QuestionChangePayload,
  cycle: string,
) => {
  const changes = Array.isArray(payload.changes) ? payload.changes : [];
  const titleChange = changes.find((change) => change.field === "title");
  const questionCount = Array.isArray(payload.question_ids)
    ? payload.question_ids.length
    : 0;
  return [
    // A title change already names the question, twice.
    !titleChange && payload.title ? quoted(payload.title) : null,
    verb.startsWith("questions.section_") && questionCount > 0
      ? `${questionCount} ${questionCount === 1 ? "question" : "questions"}`
      : null,
    titleChange
      ? `title changed from ${quoted(titleChange.from)} to ${quoted(titleChange.to)}`
      : null,
    changes.some((change) => change.field === "description")
      ? "description changed"
      : null,
    payload.scope === "everywhere"
      ? "everywhere"
      : `this ${cycle.toLowerCase()} only`,
  ].filter((part): part is string => !!part);
};
